import moment from 'moment';
import { PricePerClientFiltersFormType } from '../types/pricePerClient';

export interface PricePerClientColumnVisibility {
  client: boolean;
  kmMin: boolean;
  kmMinPrice: boolean;
  hourWaitingPrice: boolean;
  vtoActual: boolean;
  vtoAnterior: boolean;
  precioOutsite: boolean;
  item: boolean;
  ejecutivo: boolean;
  vip: boolean;
}

export const PricePerClientColumnLabels: Record<keyof PricePerClientColumnVisibility, string> = {
  client: 'Cliente',
  kmMin: 'Km Mínimo',
  kmMinPrice: 'Km Mínimo $',
  hourWaitingPrice: 'Espera $/h',
  vtoActual: 'Vencimiento Actual',
  vtoAnterior: 'Vencimiento Anterior',
  precioOutsite: 'Precio Outsite',
  item: 'Item',
  ejecutivo: 'Ejecutivo',
  vip: 'VIP'
}

export interface SortState {
    sortDir: string;
    sortBy: string;
}

export interface PricePerClientState {
    filters: PricePerClientFiltersFormType;
    page: number;
    rowsPerPage: number;
    sort: SortState;
    columnVisibility: PricePerClientColumnVisibility;
    lastAccessDate?: string;
}

const STORAGE_KEY = 'pricePerClientState';

export const getDefaultFilters = (): PricePerClientFiltersFormType => ({
    search: ''
});

export const getDefaultColumnVisibility = (): PricePerClientColumnVisibility => ({
    client: true,
    kmMin: true,
    kmMinPrice: true,
    hourWaitingPrice: true,
    vtoActual: true,
    vtoAnterior: true,
    precioOutsite: true,
    item: true,
    ejecutivo: true,
    vip: true
});

export const getDefaultSort = (): SortState => ({
    sortDir: 'ASC',
    sortBy: 'client'
});

export const getDefaultState = (): PricePerClientState => ({
    filters: getDefaultFilters(),
    page: 1,
    rowsPerPage: 0,
    sort: getDefaultSort(),
    columnVisibility: getDefaultColumnVisibility()
});

const deserializeState = (
    storedState: Partial<PricePerClientState>
): PricePerClientState => {
    const defaultFilters = getDefaultFilters();
    const today = moment().format('YYYY-MM-DD');
    const lastAccessDate = storedState.lastAccessDate;
    const isNewDay = !lastAccessDate || lastAccessDate !== today;
    
    return {
        ...getDefaultState(),
        ...storedState,
        filters: {
            ...defaultFilters,
            ...(storedState.filters || {}),
        },
        lastAccessDate: today
    };
};

export const loadStateFromLocalStorage = (): PricePerClientState => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            return deserializeState(parsed);
        }
    } catch (error) {
        console.error('Error loading price per client state:', error);
    }
    return getDefaultState();
};

export const saveStateToLocalStorage = (state: PricePerClientState): void => {
    try {
        const stateWithDate = {
            ...state,
            lastAccessDate: moment().format('YYYY-MM-DD')
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(stateWithDate));
    } catch (error) {
        console.error('Error saving price per client state:', error);
    }
};

export const clearStateFromLocalStorage = (): void => {         
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
        console.error('Error clearing price per client state:', error);
    }
};